// Global Instructions Rule Applied!

import React, { useMemo } from 'react'
import { Avatar, Typography, Tag } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { UNKNOWN_CONTACT, SECTION_COMPANY_TEAM } from '../../../state_manager/constants/strings.ts'
import { RELATIONSHIP_TYPE_ADVISOR, RELATIONSHIP_TYPE_OTHER } from '../../../state_manager/globals.ts'

const { Link } = Typography

interface UserCardListMember {
  key: string
  contact: {
    value: string
    label: string
    data?: {
      first_name?: string
      last_name?: string
      display_name?: string
      preferred_name?: string
      email?: string
      photo_url?: string
    }
  }
  founder: boolean
  employeeRelationshipId: string | null
  founderRelationshipId: string | null
  jobTitle: string
  category: string | null // Single category for backward compatibility
  categories?: string[] // Array of category IDs for multiple badges
  email: string
  contactNumber: string
  photo_url?: string | null
}

interface UserCardListProps {
  members?: UserCardListMember[]
  title?: string
  onMemberClick?: (member: UserCardListMember) => void
}

const getInitials = (member: UserCardListMember): string => {
  const contactData = member.contact?.data
  const firstName = contactData?.first_name || contactData?.preferred_name || ''
  const lastName = contactData?.last_name || ''

  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  if (firstName) {
    return firstName.substring(0, 2).toUpperCase()
  }

  const label = member.contact?.label || member.email || ''
  if (label) {
    const parts = label.trim().split(' ')
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    }
    return label.substring(0, 2).toUpperCase()
  }

  return '??'
}

const getContactColor = (contactId: string): string => {
  const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#6366f1', '#ef4444', '#f59e0b', '#14b8a6']
  const hash = contactId
    .toString()
    .split('')
    .reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0)
      return a & a
    }, 0)
  return colors[Math.abs(hash) % colors.length]
}

export function UserCardList({ members = [], title = SECTION_COMPANY_TEAM, onMemberClick }: UserCardListProps) {
  const navigate = useNavigate()
  const relationshipCategories = useSelector((state: any) => state.globalConfigs?.relationship_categories || [])

  const getCategoryLabel = (categoryId: string | null): string | null => {
    if (!categoryId) return null
    const category = relationshipCategories.find((cat: any) => cat.id === categoryId)
    return category?.category || null
  }

  // Helper to get relationship type ID from category ID
  const getTypeIdFromCategoryId = (categoryId: string | null | undefined): string | null => {
    if (!categoryId) return null
    const category = relationshipCategories.find((cat: any) => cat.id === categoryId)
    return category?.relationship_type_id || category?.relationshipTypeId || null
  }

  const filteredAndSortedMembers = useMemo(() => {
    // Include founders, employees (with employeeRelationshipId), or members with Advisor/Other type categories
    const filtered = members.filter((member) => {
      // Include founders
      if (member.founder === true) return true

      // Include employees (with employeeRelationshipId)
      if (member.employeeRelationshipId !== null) return true

      // Include members with Advisor or Other type categories (from prog__other_team)
      if (member.category !== null && member.category !== undefined) {
        const typeId = getTypeIdFromCategoryId(member.category)
        return typeId === RELATIONSHIP_TYPE_ADVISOR || typeId === RELATIONSHIP_TYPE_OTHER
      }

      return false
    })

    const sorted = [...filtered].sort((a, b) => {
      if (a.founder && !b.founder) return -1
      if (!a.founder && b.founder) return 1
      const nameA = a.contact?.label || a.email || ''
      const nameB = b.contact?.label || b.email || ''
      return nameA.localeCompare(nameB)
    })

    return sorted
  }, [members, relationshipCategories])

  const handleNameClick = (member: UserCardListMember, e: React.MouseEvent) => {
    e.preventDefault()
    if (onMemberClick) {
      onMemberClick(member)
    } else {
      const contactId = member.contact?.value
      if (contactId) {
        navigate(`/contacts/forms/full/${contactId}`)
      }
    }
  }

  const getTags = (member: UserCardListMember): React.ReactNode[] => {
    const tags: React.ReactNode[] = []

    // Show Founder badge if applicable
    if (member.founder) {
      tags.push(
        <Tag key='founder' color='blue'>
          Founder
        </Tag>
      )
    }

    // Show category badges (support multiple categories)
    const categoriesToShow =
      member.categories && member.categories.length > 0 ? member.categories : member.category ? [member.category] : []

    categoriesToShow.forEach((categoryId, index) => {
      const categoryLabel = getCategoryLabel(categoryId)
      // Don't show category tag if it's "Founder" since we already show the founder tag
      if (categoryLabel && categoryLabel.toLowerCase() !== 'founder') {
        tags.push(
          <Tag key={`category-${index}`} color='orange'>
            {categoryLabel}
          </Tag>
        )
      }
    })

    return tags
  }

  if (filteredAndSortedMembers.length === 0) {
    return null
  }

  return (
    <div
      data-debug='user-card-list-container'
      data-component='UserCardList'
      className='mb-2 mt-1'
      style={{ width: '100%' }}
    >
      {title && <div className='text-sm font-semibold text-gray-900 mb-2'>{title}</div>}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '6px',
          width: '100%'
        }}
      >
        {filteredAndSortedMembers.map((member) => {
          const contactId = member.contact?.value || member.key
          const initials = getInitials(member)
          const avatarColor = getContactColor(contactId)
          const tags = getTags(member)
          const displayName = member.contact?.label || member.email || UNKNOWN_CONTACT
          const photoUrl = member.photo_url || member.contact?.data?.photo_url || null

          return (
            <div
              key={member.key}
              data-debug={`user-card-${member.key}`}
              className='rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow bg-white flex items-center'
              style={{
                height: '76px',
                padding: '10px',
                gap: '8px',
                minWidth: '150px'
              }}
            >
              <div className='flex items-center flex-shrink-0'>
                <Avatar
                  size={32}
                  src={photoUrl || undefined}
                  style={{
                    backgroundColor: photoUrl ? undefined : avatarColor,
                    color: photoUrl ? undefined : '#FFFFFF'
                  }}
                >
                  {photoUrl ? '' : initials}
                </Avatar>
              </div>
              <div className='flex-1 min-w-0 flex flex-col justify-center gap-1'>
                <Link
                  onClick={(e) => handleNameClick(member, e)}
                  className='text-xs font-medium text-blue-600 hover:text-blue-800 cursor-pointer block truncate'
                  title={displayName}
                >
                  {displayName}
                </Link>
                {tags.length > 0 && (
                  <div className='flex flex-wrap gap-1'>
                    {tags.map((tag, index) => (
                      <span key={index} className='text-[10px]'>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
